import { Injectable, Type } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { HspModal } from './hsp-modal.interface';

export interface HspModalResult {
    status: boolean;
    output?: any;
}

export interface HspModalData {
    component: Type<HspModal>;
    width: number;
    title: string;
    input?: any;
    confirm?: string;
    cancel?: string;
}

@Injectable({
    providedIn: 'root'
})
export class HspModalService {
    private _input: any;

    public constructor(private readonly modalEvents: HspModalEventsService) {}

    /**
     * Open a new Modal with the format and content specified by the options passed by parameter.
     *
     * @param modalData format and data used to open the modal.
     * @returns Observable returning the modal result when it closes.
     */
    public open<T>(modalData: HspModalData): Observable<HspModalResult> {
        this.modalEvents.notifyModalOpen(modalData);
        this._input = modalData.input as T;
        return this.modalEvents.modalClose.pipe(take(1));
    }

    /**
     * Method used to obtain the input of the openend Modal, if it has any.
     *
     * @returns The input passed to the Modal at its opening.
     */
    public getInput<T>(): T {
        return this._input as T;
    }

    /**
     * Closes the currently opened Modal, with the output passed bu parameter
     *
     * @param result Output of the closed modal.
     */
    public close(result: HspModalResult): void {
        this.modalEvents.notifyModalClose(result);
    }
}

@Injectable({
    providedIn: 'root'
})
export class HspModalEventsService {
    private readonly modalOpen$: Subject<HspModalData> = new Subject();
    private readonly modalClose$: Subject<HspModalResult> = new Subject();

    public get modalOpen(): Observable<HspModalData> {
        return this.modalOpen$.asObservable();
    }

    public get modalClose(): Observable<HspModalResult> {
        return this.modalClose$.asObservable();
    }

    public notifyModalOpen(data: HspModalData): void {
        this.modalOpen$.next(data);
    }

    public notifyModalClose(result: HspModalResult): void {
        this.modalClose$.next(result);
    }
}
